import React, { useState, useEffect } from 'react';
import { Users, Copy, Plus, Trash2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useFirebase } from '@/providers/FirebaseProvider';
import { useStarData } from '@/hooks/useStarData';
import { toast } from 'sonner';
import { FamilyMember, FamilyInvite } from '@/utils/storage';

export const FamilyManagement: React.FC = () => {
  const {
    user,
    createFamily,
    joinFamily,
    generateInviteCode,
    revokeInvite,
    getUserFamilyId,
  } = useFirebase();
  const { data } = useStarData();

  const [familyId, setFamilyId] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isJoinDialogOpen, setIsJoinDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load family ID on mount
  useEffect(() => {
    const loadFamilyId = async () => {
      if (user) {
        const id = await getUserFamilyId();
        setFamilyId(id);
      }
    };
    loadFamilyId();
  }, [user, getUserFamilyId]);

  // Update familyId when data changes
  useEffect(() => {
    if (data?.familyId) {
      setFamilyId(data.familyId);
    }
  }, [data?.familyId]);

  const handleCreateFamily = async () => {
    if (!data) {
      toast.error('No data available to create family');
      return;
    }

    setLoading(true);
    try {
      const newFamilyId = await createFamily(data);
      setFamilyId(newFamilyId);
      setIsCreateDialogOpen(false);
      // Reload page to fetch updated data
      window.location.reload();
    } catch (error) {
      console.error('Error creating family:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinFamily = async () => {
    if (!joinCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }

    setLoading(true);
    try {
      await joinFamily(joinCode.trim().toUpperCase());
      setIsJoinDialogOpen(false);
      setJoinCode('');
      // Reload page to fetch updated data
      window.location.reload();
    } catch (error) {
      console.error('Error joining family:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvite = async () => {
    setLoading(true);
    try {
      const code = await generateInviteCode();
      setInviteCode(code);
      setIsInviteDialogOpen(true);
    } catch (error) {
      console.error('Error generating invite:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied to clipboard!');
  };

  const handleRevokeInvite = async (code: string) => {
    try {
      await revokeInvite(code);
    } catch (error) {
      console.error('Error revoking invite:', error);
    }
  };

  const members = data?.familyMembers ? Object.values(data.familyMembers) : [];
  const invites = data?.familyInvites ? Object.values(data.familyInvites) : [];
  const isOwner = data?.familyMetadata?.owner === user?.uid;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Family Sharing
        </CardTitle>
        <CardDescription>
          Share chores and rewards with other parents
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!familyId ? (
          // Not in a family - show create/join options
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              You're not part of a family yet. Create a new family or join an existing one.
            </p>
            <div className="flex gap-2">
              <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="default" className="flex-1">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Family
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create a Family</DialogTitle>
                    <DialogDescription>
                      You'll become the host and can invite other parents to manage chores together.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <p className="text-sm text-muted-foreground">
                      Your current data will be shared with family members. You can generate invite codes after creating the family.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateFamily} disabled={loading}>
                      {loading ? 'Creating...' : 'Create Family'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={isJoinDialogOpen} onOpenChange={setIsJoinDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex-1">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Join Family
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Join a Family</DialogTitle>
                    <DialogDescription>
                      Enter the invite code from another parent to join their family.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="joinCode">Invite Code</Label>
                      <Input
                        id="joinCode"
                        placeholder="ABC123"
                        value={joinCode}
                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                        maxLength={6}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsJoinDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleJoinFamily} disabled={loading || !joinCode.trim()}>
                      {loading ? 'Joining...' : 'Join Family'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        ) : (
          // In a family - show family info
          <div className="space-y-4">
            {/* Family Members */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Family Members ({members.length})</h4>
              <div className="space-y-2">
                {members.map((member: FamilyMember) => (
                  <div key={member.userId} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2">
                      {member.photoURL && (
                        <img src={member.photoURL} alt={member.displayName} className="w-8 h-8 rounded-full" />
                      )}
                      <div>
                        <p className="text-sm font-medium">{member.displayName}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
                      {member.role === 'owner' ? 'Host' : 'Parent'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Invite Management */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Invite Codes</h4>
                <Button size="sm" onClick={handleGenerateInvite} disabled={loading}>
                  <Plus className="mr-2 h-3 w-3" />
                  Generate Code
                </Button>
              </div>

              {invites.length > 0 ? (
                <div className="space-y-2">
                  {invites.map((invite: FamilyInvite) => (
                    <div key={invite.code} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                      <div>
                        <p className="font-mono text-sm font-medium">{invite.code}</p>
                        <p className="text-xs text-muted-foreground">
                          {invite.status === 'active' && 'Active'}
                          {invite.status === 'used' && `Used by ${invite.usedBy}`}
                          {invite.status === 'revoked' && 'Revoked'}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {invite.status === 'active' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCopyInviteCode(invite.code)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            {isOwner && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRevokeInvite(invite.code)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </>
                        )}
                        <Badge
                          variant={
                            invite.status === 'active' ? 'default' :
                            invite.status === 'used' ? 'secondary' :
                            'destructive'
                          }
                        >
                          {invite.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No invite codes yet. Generate one to invite other parents.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Dialog for showing generated invite code */}
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Code Generated</DialogTitle>
              <DialogDescription>
                Share this code with another parent so they can join your family.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <div className="flex-1 p-4 bg-muted rounded-lg">
                  <p className="text-2xl font-mono font-bold text-center">{inviteCode}</p>
                </div>
                <Button variant="outline" onClick={() => handleCopyInviteCode(inviteCode)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                This code can be used once to join your family. You can revoke it anytime from the invite list.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => setIsInviteDialogOpen(false)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
